'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  folderService, listProjects, createProject, deleteProject, moveProject,
  renameProject, shareProject, sendProjectFile,
  listNotifications, markNotifRead, importSharedProject,
  getProfile, updateProfile, changePassword,
  listOrgUsers, updateUserAccess, inviteUser, removeUser,
  listMembers, inviteMember, updateMemberRole, removeMember
} from '@/shell/services/api.service';
import { useTr } from '@/shell/hooks';
import type {
  TreeItem, Member, Notification, OrgUser, Profile, StatusMessage,
  FolderNavigatorProps, MindmapInstance
} from '@/types';
import type { TranslationValue } from '@inno-ck/shared';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface InlineInputState {
  show: boolean;
  type: 'project' | 'folder' | 'whiteboard' | 'textdoc';
  value: string;
  parentId: string | null;
}

interface RenameState {
  show: boolean;
  target: TreeItem | null;
  value: string;
}

interface CtxMenuLocal {
  show: boolean;
  x: number;
  y: number;
  target: TreeItem | null;
}

// ---------------------------------------------------------------------------
// Sub-component props
// ---------------------------------------------------------------------------

interface ProjectRowProps {
  readonly project: any;
  readonly depth: number;
  readonly selected: TreeItem | null;
  readonly onSelect: (item: TreeItem) => void;
  readonly onOpen: (project: any) => void;
  readonly onCtx: (e: React.MouseEvent, item: TreeItem) => void;
}

interface FolderRowProps {
  readonly folder: any;
  readonly depth: number;
  readonly expanded: Record<string, boolean>;
  readonly selected: TreeItem | null;
  readonly allFolders: any[];
  readonly allProjects: any[];
  readonly inlineInput: InlineInputState;
  readonly onToggle: (id: string) => void;
  readonly onSelect: (item: TreeItem) => void;
  readonly onOpen: (project: any) => void;
  readonly onCtx: (e: React.MouseEvent, item: TreeItem) => void;
  readonly onProjectDrop: (projectId: string, folderId: string | null) => void;
  readonly inlineValue: string;
  readonly onInlineValueChange: (v: string) => void;
  readonly onConfirmInline: () => void;
  readonly onCancelInline: () => void;
}

// ---------------------------------------------------------------------------
// DisclosureTriangle — expand/collapse pictogram
//   collapsed → solid black triangle pointing right
//   expanded  → white (outlined) triangle pointing down
// ---------------------------------------------------------------------------

function DisclosureTriangle({ open }: { open: boolean }) {
  return open ? (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className="block">
      <polygon points="1.5,3 8.5,3 5,8.5" fill="#fff" stroke="#333" strokeWidth="1.1" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className="block">
      <polygon points="3,1.5 8.5,5 3,8.5" fill="#111" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// ProjectRow
// ---------------------------------------------------------------------------

function ProjectRow({ project, depth, selected, onSelect, onOpen, onCtx }: ProjectRowProps) {
  return (
    <div
      className={`tree-row flex items-center gap-1.5 py-0.5 pr-1.5 cursor-pointer rounded-sm mx-1 my-px min-h-[26px] text-[13px] hover:bg-[#e4e4e4] ${selected?.id === project.id ? 'bg-[#cde0f7]' : ''}`}
      style={{ paddingLeft: 8 + depth * 16 }}
      draggable
      onClick={() => onSelect({ id: project.id, type: 'project', name: project.projectName, data: project })}
      onDoubleClick={() => onOpen(project)}
      onContextMenu={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onCtx(e, { id: project.id, type: 'project', name: project.projectName, data: project }); }}
      onDragStart={(e: React.DragEvent) => { e.stopPropagation(); e.dataTransfer.setData('text/plain', project.id); }}
    >
      {/* Spacer to align with folder arrow */}
      <span className="w-4 shrink-0" />
      {project.jsonData?.projectType === 'whiteboard'
        ? <span className="w-4 h-4 shrink-0 inline-flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2.5"/>
              <path d="M6 15 Q9 11 12 14 Q15 17 18 12" strokeWidth="2.2"/>
              <line x1="15" y1="4" x2="19" y2="0.5"/>
              <rect x="17.5" y="-0.5" width="5" height="3" rx="0.7" transform="rotate(35 17.5 -0.5)"/>
            </svg>
          </span>
        : project.jsonData?.projectType === 'textdoc'
          ? <span className="w-4 h-4 shrink-0 inline-flex items-center justify-center">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="8" y1="13" x2="16" y2="13"/>
                <line x1="8" y1="17" x2="16" y2="17"/>
                <line x1="8" y1="9" x2="11" y2="9"/>
              </svg>
            </span>
          : <span className="w-4 shrink-0 text-center text-[13px] leading-none">💡</span>
      }
      <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{project.projectName}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FolderRow
// ---------------------------------------------------------------------------

function FolderRow({ folder, depth, expanded, selected, allFolders, allProjects, inlineInput, onToggle, onSelect, onOpen, onCtx, onProjectDrop, inlineValue, onInlineValueChange, onConfirmInline, onCancelInline }: FolderRowProps) {
  const [dropOver, setDropOver] = useState<boolean>(false);
  const tr = useTr();
  const directProjects = allProjects.filter((p: any) => p.folderId === folder.id && !p.parentId);
  const sortedDirectProjects = [...directProjects].sort((a, b) => a.projectName.localeCompare(b.projectName, 'fr', { sensitivity: 'base' }));
  const subFolders = allFolders.filter((f: any) => f.parentId === folder.id);
  const sortedSubFolders = [...subFolders].sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
  const isExpanded = !!expanded[folder.id];
  const isSelected = selected?.id === folder.id;

  return (
    <div>
      <div
        className={`tree-row flex items-center gap-1.5 py-0.5 pr-1.5 cursor-pointer rounded-sm mx-1 my-px min-h-[26px] text-[13px] hover:bg-[#e4e4e4] ${isSelected ? 'bg-[#cde0f7]' : ''} ${dropOver ? 'bg-[#d0e8ff] outline-2 outline-dashed outline-[#5588cc]' : ''}`}
        style={{ paddingLeft: 8 + depth * 16 }}
        onClick={() => onSelect({ id: folder.id, type: 'folder', name: folder.name, data: folder })}
        onContextMenu={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onCtx(e, { id: folder.id, type: 'folder', name: folder.name, data: folder }); }}
        onDragOver={(e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDropOver(true); }}
        onDragLeave={(e: React.DragEvent) => { e.stopPropagation(); setDropOver(false); }}
        onDrop={(e: React.DragEvent) => { e.stopPropagation(); setDropOver(false); const id = e.dataTransfer.getData('text/plain'); if (id) onProjectDrop(id, folder.id); }}
      >
        <span className="w-4 shrink-0 flex items-center justify-center cursor-pointer" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onToggle(folder.id); }}>
          <DisclosureTriangle open={isExpanded} />
        </span>
        <span className="w-4 shrink-0 text-center text-[13px] leading-none">📁</span>
        <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{folder.name}</span>
        <span className="text-[#5588cc] text-[11px] shrink-0">({sortedSubFolders.length + sortedDirectProjects.length})</span>
      </div>

      {isExpanded && (
        <>
          {inlineInput.show && inlineInput.parentId === folder.id && (
            <div className="flex items-center pr-1.5 py-0.5 mx-1 my-px" style={{ paddingLeft: 8 + (depth + 1) * 16 }}>
              <input
                autoFocus
                value={inlineValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInlineValueChange(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); onConfirmInline(); } if (e.key === 'Escape') onCancelInline(); }}
                onBlur={onConfirmInline}
                className="flex-1 border border-[#7aacf5] rounded-sm px-1.5 py-0.5 text-xs outline-none bg-white"
                placeholder={String(inlineInput.type === 'folder' ? tr.folderPlaceholder : tr.projectPlaceholder)}
              />
            </div>
          )}
          {sortedSubFolders.map((sub: any) => (
            <FolderRow key={sub.id} folder={sub} depth={depth + 1}
              expanded={expanded} selected={selected} allFolders={allFolders} allProjects={allProjects}
              inlineInput={inlineInput} onToggle={onToggle} onSelect={onSelect} onOpen={onOpen}
              onCtx={onCtx} onProjectDrop={onProjectDrop}
              inlineValue={inlineValue} onInlineValueChange={onInlineValueChange}
              onConfirmInline={onConfirmInline} onCancelInline={onCancelInline}
            />
          ))}
          {sortedDirectProjects.map((p: any) => (
            <ProjectRow key={p.id} project={p} depth={depth + 1}
              selected={selected} onSelect={onSelect} onOpen={onOpen} onCtx={onCtx} />
          ))}
          {sortedSubFolders.length === 0 && sortedDirectProjects.length === 0 && !inlineInput.show && (
            <div className="text-[#aaa] text-[11px] italic py-0.5" style={{ paddingLeft: 8 + (depth + 1) * 16 + 20 }}>{String(tr.empty)}</div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function FolderNavigator({ userId, onOpenProject, currentProject, mindRef, lang, setLang, sidebarOpen, onLogout, freeMode = false, onExportWorkbook, onImportWorkbook }: FolderNavigatorProps) {
  const [folders, setFolders] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ __inbox__: true });
  const [selected, setSelected] = useState<TreeItem | null>(null);
  const [inlineInput, setInlineInput] = useState<InlineInputState>({ show: false, type: 'project', value: '', parentId: null });
  const [ctxMenu, setCtxMenu] = useState<CtxMenuLocal>({ show: false, x: 0, y: 0, target: null });
  const [renameState, setRenameState] = useState<RenameState>({ show: false, target: null, value: '' });
  const [inboxDropOver, setInboxDropOver] = useState<boolean>(false);
  const [showAbout, setShowAbout] = useState<boolean>(false);
  const [showUpgrade, setShowUpgrade] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showMembers, setShowMembers] = useState<boolean>(false);
  const [members, setMembers] = useState<any[]>([]);
  const [memberTab, setMemberTab] = useState<string>('members');
  const [memberFilter, setMemberFilter] = useState<string>('');
  const [memberInvite, setMemberInvite] = useState<{ email: string; role: string }>({ email: '', role: 'member' });
  const [memberStatus, setMemberStatus] = useState<StatusMessage | null>(null);
  const [showAdmin, setShowAdmin] = useState<boolean>(false);
  const [orgUsers, setOrgUsers] = useState<any[]>([]);
  const [adminStatus, setAdminStatus] = useState<StatusMessage | null>(null);
  const [inviteForm, setInviteForm] = useState<{ email: string; role: string }>({ email: '', role: 'user' });
  const [profile, setProfile] = useState<Profile>({ firstName: '', lastName: '', organisation: '', email: '' });
  const [profileStatus, setProfileStatus] = useState<StatusMessage | null>(null);
  const [pwdFields, setPwdFields] = useState<{ old: string; new: string; confirm: string }>({ old: '', new: '', confirm: '' });
  const [pwdStatus, setPwdStatus] = useState<StatusMessage | null>(null);
  const [showExport, setShowExport] = useState<boolean>(false);
  const [showShare, setShowShare] = useState<boolean>(false);
  const [shareTarget, setShareTarget] = useState<string>('');
  const [shareFormat, setShareFormat] = useState<string | null>(null);
  const [shareSuggestions, setShareSuggestions] = useState<any[]>([]);
  const [shareStatus, setShareStatus] = useState<StatusMessage | null>(null);
  const [shareSending, setShareSending] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showNotifs, setShowNotifs] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [notifStatuses, setNotifStatuses] = useState<Record<string, string>>({});
  const [showImport, setShowImport] = useState<boolean>(false);
  const [importLink, setImportLink] = useState<string>('');
  const [importStatus, setImportStatus] = useState<StatusMessage | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const exportBtnRef = useRef<HTMLDivElement>(null);
  const notifBtnRef = useRef<HTMLButtonElement>(null);
  const tr = useTr();

  const unreadCount = notifications.filter((n: any) => !n.read).length;

  const loadAll = useCallback(async () => {
    if (!userId) return;
    const [f, p] = await Promise.all([folderService.list(userId), listProjects(userId)]);
    setFolders(f || []);
    setProjects(p || []);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    getProfile(userId).then((p: any) => {
      if (p) setProfile({ firstName: p.firstName || '', lastName: p.lastName || '', organisation: p.organisation || '', email: p.email || '' });
    }).catch(() => {});
  }, [userId]);

  const openMembers = async (): Promise<void> => {
    setShowMembers(true); setMemberStatus(null); setMemberFilter('');
    try { setMembers(await listMembers()); } catch { setMembers([]); }
  };

  const handleInviteMember = async (): Promise<void> => {
    if (!memberInvite.email.trim()) return;
    setMemberStatus(null);
    try {
      const m = await inviteMember(memberInvite);
      setMembers((ms: any[]) => [...ms, m]);
      setMemberStatus({ ok: true, msg: String(tr.membersInviteOk) });
      setMemberInvite({ email: '', role: 'member' });
    } catch { setMemberStatus({ ok: false, msg: String(tr.membersInviteErr) }); }
  };

  const handleMemberRole = async (m: any, role: string): Promise<void> => {
    try {
      await updateMemberRole(m._id, role);
      setMembers((ms: any[]) => ms.map((x: any) => x._id === m._id ? { ...x, role } : x));
    } catch { setMemberStatus({ ok: false, msg: String(tr.membersInviteErr) }); }
  };

  const handleRemoveMember = async (m: any): Promise<void> => {
    const name = `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email;
    if (!window.confirm(typeof tr.adminRemoveConfirm === 'function' ? tr.adminRemoveConfirm(name) : String(tr.adminRemoveConfirm))) return;
    try {
      await removeMember(m._id);
      setMembers((ms: any[]) => ms.filter((x: any) => x._id !== m._id));
    } catch { setMemberStatus({ ok: false, msg: String(tr.membersInviteErr) }); }
  };

  const openAdmin = async (): Promise<void> => {
    setShowAdmin(true);
    setAdminStatus(null);
    try { setOrgUsers(await listOrgUsers()); } catch { setOrgUsers([]); }
  };

  const handleToggleUser = async (u: any): Promise<void> => {
    setAdminStatus(null);
    try {
      const updated: any = await updateUserAccess(u._id, { active: !u.active });
      setOrgUsers((us: any[]) => us.map((x: any) => x._id === u._id ? { ...x, active: updated.active ?? !u.active } : x));
    } catch { setAdminStatus({ ok: false, msg: String(tr.adminError) }); }
  };

  const handleChangeAccess = async (u: any, access: string): Promise<void> => {
    setAdminStatus(null);
    try {
      await updateUserAccess(u._id, { access });
      setOrgUsers((us: any[]) => us.map((x: any) => x._id === u._id ? { ...x, access } : x));
    } catch { setAdminStatus({ ok: false, msg: String(tr.adminError) }); }
  };

  const handleRemoveUser = async (u: any): Promise<void> => {
    const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
    if (!window.confirm(typeof tr.adminRemoveConfirm === 'function' ? tr.adminRemoveConfirm(name) : String(tr.adminRemoveConfirm))) return;
    try {
      await removeUser(u._id);
      setOrgUsers((us: any[]) => us.filter((x: any) => x._id !== u._id));
    } catch { setAdminStatus({ ok: false, msg: String(tr.adminError) }); }
  };

  const handleInvite = async (): Promise<void> => {
    if (!inviteForm.email.trim()) return;
    setAdminStatus(null);
    try {
      const newUser = await inviteUser(inviteForm);
      setOrgUsers((us: any[]) => [...us, newUser]);
      setAdminStatus({ ok: true, msg: String(tr.adminInviteOk) });
      setInviteForm({ email: '', role: 'user' });
    } catch { setAdminStatus({ ok: false, msg: String(tr.adminInviteErr) }); }
  };

  const handleSaveProfile = async (): Promise<void> => {
    setProfileStatus(null);
    try {
      await updateProfile(userId, profile);
      setProfileStatus({ ok: true, msg: String(tr.profileSaved) });
    } catch { setProfileStatus({ ok: false, msg: String(tr.profileError) }); }
  };

  const handleChangePassword = async (): Promise<void> => {
    setPwdStatus(null);
    if (pwdFields.new !== pwdFields.confirm) { setPwdStatus({ ok: false, msg: String(tr.pwdMismatch) }); return; }
    try {
      await changePassword(pwdFields.old, pwdFields.new);
      setPwdStatus({ ok: true, msg: String(tr.pwdChanged) });
      setPwdFields({ old: '', new: '', confirm: '' });
    } catch { setPwdStatus({ ok: false, msg: String(tr.pwdError) }); }
  };

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await listNotifications();
      setNotifications(Array.isArray(data) ? data : []);
    } catch { /* API may not exist yet — silent fail */ }
  }, [userId]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (userId) loadAll();
  }, [userId]);

  useEffect(() => {
    fetchNotifications();
    const timer = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(timer);
  }, [fetchNotifications]);

  useEffect(() => {
    const close = () => setCtxMenu((c: CtxMenuLocal) => ({ ...c, show: false }));
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  useEffect(() => {
    if (renameState.show) renameInputRef.current?.focus();
  }, [renameState.show]);

  const toggle = (id: string): void => setExpanded((e: Record<string, boolean>) => ({ ...e, [id]: !e[id] }));
  const rootFolders = folders.filter((f: any) => !f.parentId);
  const sortedRootFolders = [...rootFolders].sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
  const rootProjects = projects.filter((p: any) => !p.folderId && !p.parentId);
  const sortedRootProjects = [...rootProjects].sort((a, b) => a.projectName.localeCompare(b.projectName, 'fr', { sensitivity: 'base' }));

  const startCreate = (parentId: string | null, type: string): void => {
    setCtxMenu((c: CtxMenuLocal) => ({ ...c, show: false }));
    if (parentId === '__inbox__') setExpanded((e: Record<string, boolean>) => ({ ...e, __inbox__: true }));
    else if (parentId) setExpanded((e: Record<string, boolean>) => ({ ...e, [parentId]: true }));
    setInlineInput({ show: true, type: type as InlineInputState['type'], value: '', parentId });
  };

  const confirmInline = async (): Promise<void> => {
    const name = inlineInput.value.trim();
    if (!name) { cancelInline(); return; }
    const { type, parentId } = inlineInput;
    if (type === 'folder') {
      const fId = folders.find((f: any) => f.id === parentId) ? parentId : null;
      await folderService.create(userId, name, fId);
    } else {
      const isFolder = parentId === '__inbox__' || folders.find((f: any) => f.id === parentId);
      const folderId = isFolder && parentId !== '__inbox__' ? parentId : null;
      const projectType = type === 'whiteboard' ? 'whiteboard' : type === 'textdoc' ? 'textdoc' : 'ck';
      const jsonData = projectType === 'textdoc'
        ? { projectType: 'textdoc', content: null }
        : { label: name, nodes: [], patents: [], projectType };
      await createProject({ projectName: name, userId, jsonData, folderId, parentId: null } as any);
    }
    cancelInline();
    await loadAll();
  };

  const cancelInline = (): void => setInlineInput({ show: false, type: 'project', value: '', parentId: null });

  const openCtx = (e: React.MouseEvent, target: TreeItem): void => {
    e.preventDefault();
    setCtxMenu({ show: true, x: e.clientX, y: e.clientY, target });
    setSelected(target);
  };

  const renameItem = (item: TreeItem): void => {
    setCtxMenu((c: CtxMenuLocal) => ({ ...c, show: false }));
    setRenameState({ show: true, target: item, value: item.name || (item.data as any)?.projectName || '' });
  };

  const confirmRename = async (): Promise<void> => {
    const name = renameState.value.trim();
    const item = renameState.target;
    setRenameState({ show: false, target: null, value: '' });
    if (!name || !item) return;
    if (item.type === 'folder') await folderService.update(item.id, name);
    else await renameProject(item.id, name);
    await loadAll();
  };

  const duplicateItem = async (item: TreeItem): Promise<void> => {
    setCtxMenu((c: CtxMenuLocal) => ({ ...c, show: false }));
    const src = item.data as any;
    await createProject({ projectName: src.projectName + ' (copie)', userId, jsonData: src.jsonData, folderId: src.folderId || null } as any);
    await loadAll();
  };

  const deleteItem = async (item: TreeItem): Promise<void> => {
    if (!window.confirm(typeof tr.deleteConfirm === 'function' ? tr.deleteConfirm(item.name) : String(tr.deleteConfirm))) return;
    setCtxMenu((c: CtxMenuLocal) => ({ ...c, show: false }));
    if (item.type === 'folder') await folderService.remove(item.id);
    else await deleteProject({ projectName: (item.data as any).projectName, userId });
    setSelected(null);
    await loadAll();
  };

  const handleProjectDrop = async (projectId: string, folderId: string | null): Promise<void> => {
    await moveProject(projectId, folderId || null);
    await loadAll();
  };

  const downloadBlob = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const handleExport = (format: string): void => {
    const svgEl = mindRef.current?.svgElRef?.current;
    if (!svgEl) return;
    setShowExport(false);
    const clone = svgEl.cloneNode(true) as SVGElement;
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const svgStr = new XMLSerializer().serializeToString(clone);
    const name = (currentProject as any)?.projectName || 'frugal-design';

    if (format === 'svg') {
      downloadBlob(new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' }), `${name}.svg`);
    } else if (format === 'png') {
      const w = (svgEl as any).viewBox?.baseVal?.width || +(svgEl.getAttribute('width') || 1400);
      const h = (svgEl as any).viewBox?.baseVal?.height || +(svgEl.getAttribute('height') || 900);
      const scale = 3;
      const canvas = document.createElement('canvas');
      canvas.width = w * scale; canvas.height = h * scale;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      const img = new Image();
      const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob((b: Blob | null) => { if (b) downloadBlob(b, `${name}.png`); }, 'image/png');
      };
      img.src = url;
    } else if (format === 'pdf') {
      const win = window.open('', '_blank')!;
      const svgContent = svgStr;
      win.document.write(`<!DOCTYPE html><html><head><style>
        *{margin:0;padding:0} body{background:#fff}
        svg{width:100%;height:auto;display:block}
        @media print{@page{size:landscape;margin:8mm}}
      </style></head><body>${svgContent}</body></html>`);
      win.document.close();
      setTimeout(() => { win.print(); }, 600);
    }
  };

  /* ---- Notifications ---- */
  const handleImportFromNotif = async (notif: any): Promise<void> => {
    setNotifStatuses((s: Record<string, string>) => ({ ...s, [notif._id]: 'loading' }));
    try {
      if (notif.type === 'folder_shared') {
        await folderService.create(userId, notif.folderName || notif.projectName || 'Dossier partage', null);
      } else {
        await importSharedProject(notif.projectId, userId);
      }
      await markNotifRead(notif._id);
      setNotifications((ns: any[]) => ns.map((n: any) => n._id === notif._id ? { ...n, read: true } : n));
      setNotifStatuses((s: Record<string, string>) => ({ ...s, [notif._id]: 'ok' }));
      await loadAll();
    } catch {
      setNotifStatuses((s: Record<string, string>) => ({ ...s, [notif._id]: 'err' }));
    }
  };

  const handleMarkRead = async (notif: any): Promise<void> => {
    try {
      await markNotifRead(notif._id);
      setNotifications((ns: any[]) => ns.map((n: any) => n._id === notif._id ? { ...n, read: true } : n));
    } catch { /* silent */ }
  };

  /* ---- Import from link ---- */
  const extractId = (str: string): string => {
    const byParam = str.match(/[?&]id=([a-f0-9]{24})/i);
    if (byParam) return byParam[1]!;
    const bySlash = str.match(/([a-f0-9]{24})(?:[/?#]|$)/i);
    if (bySlash) return bySlash[1]!;
    return str.trim();
  };

  const handleImportLink = async (): Promise<void> => {
    const id = extractId(importLink);
    if (!id) return;
    setImportStatus(null);
    try {
      await importSharedProject(id, userId);
      setImportStatus({ ok: true, msg: String(tr.importSuccess) });
      setImportLink('');
      await loadAll();
    } catch {
      setImportStatus({ ok: false, msg: String(tr.importError) });
    }
  };

  // Filter members as user types
  const onShareTargetChange = (val: string): void => {
    setShareTarget(val);
    setShareStatus(null);
    if (val.trim().length < 1) { setShareSuggestions([]); return; }
    const q = val.toLowerCase();
    setShareSuggestions(
      members.filter((m: any) =>
        `${m.firstName} ${m.lastName} ${m.email}`.toLowerCase().includes(q)
      ).slice(0, 6)
    );
  };

  // Generate SVG blob from canvas
  const buildSvgBlob = (): Blob | null => {
    const svgEl = mindRef.current?.svgElRef?.current;
    if (!svgEl) return null;
    const clone = svgEl.cloneNode(true) as SVGElement;
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    return new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' });
  };

  const buildPngBlob = (): Promise<Blob> => new Promise((resolve, reject) => {
    const svgBlob = buildSvgBlob();
    if (!svgBlob) { reject(); return; }
    const svgEl = mindRef.current!.svgElRef.current!;
    const w = (svgEl as any).viewBox?.baseVal?.width || +(svgEl.getAttribute('width') || 1400);
    const h = (svgEl as any).viewBox?.baseVal?.height || +(svgEl.getAttribute('height') || 900);
    const scale = 3;
    const canvas = document.createElement('canvas');
    canvas.width = w * scale; canvas.height = h * scale;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    const img = new Image();
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => { ctx.drawImage(img, 0, 0); URL.revokeObjectURL(url); canvas.toBlob((b: Blob | null) => { if (b) resolve(b); else reject(); }, 'image/png'); };
    img.onerror = reject;
    img.src = url;
  });

  const handleShare = async (): Promise<void> => {
    if (!currentProject?.id || !shareTarget.trim()) return;
    setShareStatus(null);
    setShareSending(true);
    const name = currentProject?.projectName || 'project';
    try {
      if (!shareFormat) {
        await shareProject(currentProject!.id, shareTarget.trim());
      } else {
        const fd = new FormData();
        fd.append('recipientEmail', shareTarget.trim());
        fd.append('projectId', currentProject!.id);
        fd.append('format', shareFormat);
        if (shareFormat === 'svg') {
          const blob = buildSvgBlob();
          if (!blob) throw new Error('no svg');
          fd.append('file', blob, `${name}.svg`);
        } else if (shareFormat === 'png') {
          const blob = await buildPngBlob();
          fd.append('file', blob, `${name}.png`);
        } else if (shareFormat === 'pdf') {
          const blob = buildSvgBlob();
          if (blob) fd.append('file', blob, `${name}.svg`);
          fd.append('convertToPdf', 'true');
        }
        await sendProjectFile(fd);
      }
      setShareStatus({ ok: true, msg: String(tr.shareSuccess) });
      setShareTarget(''); setShareSuggestions([]);
    } catch {
      setShareStatus({ ok: false, msg: String(tr.shareError) });
    } finally {
      setShareSending(false);
    }
  };

  return (
    <div 
      className={`folder-navigator w-[295px] min-w-[295px] h-full bg-[#f8f8f8] border-r border-[#e0e0e0] flex flex-col text-[13px] select-none relative overflow-hidden transition-[width,min-width] duration-[220ms] ease-in-out ${sidebarOpen ? 'sidebar-open max-lg:translate-x-0 max-lg:shadow-[4px_0_24px_rgba(0,0,0,0.18)]' : 'max-lg:-translate-x-full'} max-lg:fixed max-lg:top-0 max-lg:left-0 max-lg:h-full max-lg:z-[1000] max-lg:transition-transform max-lg:duration-[250ms] max-lg:ease-in-out max-lg:shadow-none ${collapsed ? 'sidebar-collapsed' : ''}`}
      onClick={() => { setCtxMenu((c: CtxMenuLocal) => ({ ...c, show: false })); setShowNotifs(false); setShowExport(false); }}
    >
      {/* Logo + App name + flags */}
      <div className="app-header flex items-center gap-[9px] px-3.5 pt-3 pb-2.5 bg-white border-b border-[#e8e8e8]">
        {collapsed
          ? <img src="/pictogramme_enjeu-org.png" alt="enjeu.org" className="app-logo w-8 h-8 shrink-0 object-contain" />
          : <img src="/logo2_enjeu-org.png" alt="enjeu.org" className="app-logo h-7 w-auto shrink-0 mr-auto" />
        }
        <span
          className="lang-flags flag text-[19px] cursor-pointer opacity-100 rounded px-0.5 py-px transition-opacity duration-150 leading-none hover:opacity-70"
          title={lang === 'fr' ? 'Switch to English' : lang === 'en' ? 'Cambiar a español' : 'Passer en français'}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            const next = lang === 'fr' ? 'en' : lang === 'en' ? 'es' : 'fr';
            setLang(next);
          }}
        >{lang === 'fr' ? '🇫🇷' : lang === 'en' ? '🇬🇧' : '🇪🇸'}</span>
        <button
          className="sidebar-collapse-btn border-none bg-transparent cursor-pointer px-1 py-0.5 rounded text-[#999] flex items-center justify-center shrink-0 transition-colors duration-[120ms] ml-0.5 hover:bg-[#efefef] hover:text-[#333]"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); setCollapsed((c: boolean) => !c); }}
          title={collapsed ? (lang === 'fr' ? 'Agrandir' : 'Expand') : (lang === 'fr' ? 'Reduire' : 'Collapse')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            {collapsed
              ? <polyline points="9 18 15 12 9 6" />
              : <polyline points="15 18 9 12 15 6" />
            }
          </svg>
        </button>
      </div>

      {/* User bar — hidden in free mode (no account, no team, no notifications). */}
      {!freeMode && (
      <div className="user-bar flex items-center gap-1 px-3 py-1.5 bg-white border-b border-[#e8e8e8]">
        <span className="user-avatar w-6 h-6 flex items-center justify-center text-[15px] leading-none shrink-0">👤</span>
        <span className="user-name flex-1 text-xs text-[#555] whitespace-nowrap overflow-hidden text-ellipsis">
          {(profile.firstName || profile.lastName)
            ? `${profile.firstName} ${profile.lastName.toUpperCase()}`.trim()
            : String(tr.user)
          }
        </span>

        {/* Members */}
        <button className="userbar-icon-btn relative border-none bg-transparent cursor-pointer w-7 h-7 rounded text-[#666] flex items-center justify-center transition-colors duration-[120ms] hover:bg-[#efefef]" title={String(tr.membersBtn)}
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); openMembers(); setShowNotifs(false); }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="7" r="4"/>
            <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/>
            <circle cx="17" cy="7" r="3"/>
            <path d="M21 21v-2a3 3 0 0 0-3-3h-1"/>
          </svg>
        </button>

        {/* Notifications */}
        <button className="userbar-icon-btn notif-btn relative border-none bg-transparent cursor-pointer w-7 h-7 rounded text-[#666] flex items-center justify-center transition-colors duration-[120ms] hover:bg-[#efefef]" title={String(tr.notifications)} ref={notifBtnRef}
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); setShowNotifs((v: boolean) => !v); setShowImport(false); }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          {unreadCount > 0 && <span className="notif-badge absolute -top-px -right-0.5 bg-[#e05a00] text-white text-[9px] font-bold min-w-[14px] h-[14px] rounded-full flex items-center justify-center px-[3px] pointer-events-none leading-none">{unreadCount}</span>}
        </button>

        <button className="settings-btn border-none bg-transparent cursor-pointer w-7 h-7 flex items-center justify-center rounded text-[#666] text-base leading-none transition-colors duration-[120ms] hover:bg-[#efefef]" title={String(tr.settings)}
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); setShowSettings(true); }}>⚙️</button>
        {(profile.role === 'admin' || profile.isAdmin) && (
          <button className="userbar-icon-btn admin-btn relative border-none bg-transparent cursor-pointer w-7 h-7 rounded text-[#3a5a99] flex items-center justify-center transition-colors duration-[120ms] hover:!bg-[#eef2ff]" title={String(tr.adminPanel)}
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); openAdmin(); }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12z"/>
              <path d="M12 13.6c-3.2 0-9.6 1.6-9.6 4.8v1.6h19.2v-1.6c0-3.2-6.4-4.8-9.6-4.8z"/>
              <g transform="translate(13.5,2) scale(0.48)">
                <path d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.21.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
              </g>
            </svg>
          </button>
        )}
      </div>
      )}

      {/* Notification panel */}
      {showNotifs && (
        <div className="notif-panel absolute top-[var(--userbar-h,70px)] left-0 right-0 bg-white border-b border-[#e0e0e0] shadow-md z-[400] max-h-[340px] overflow-y-auto" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <div className="text-[11px] font-bold text-[#888] uppercase tracking-wide px-3 pt-2 pb-1.5 border-b border-[#f0f0f0]">{String(tr.notifications)}</div>
          {notifications.length === 0
            ? <div className="text-xs text-[#aaa] italic px-3 py-3.5 text-center">{String(tr.noNotifications)}</div>
            : notifications.map((notif: any) => {
                const st = notifStatuses[notif._id];
                const isFolder = notif.type === 'folder_shared';
                return (
                  <div key={notif._id} className={`flex gap-2 px-3 py-2 border-b border-[#f4f4f4] transition-colors duration-100 hover:bg-[#fafafa] ${notif.read ? 'opacity-50' : ''}`}>
                    <div className="text-base shrink-0 pt-px">{isFolder ? '📁' : '💡'}</div>
                    <div className="flex-1 flex flex-col gap-[3px]">
                      <div className="text-[11px] text-[#888]">
                        {typeof tr.projectSharedBy === 'function' ? tr.projectSharedBy(notif.fromUserName || notif.fromUserId || '?') : String(tr.projectSharedBy)}
                      </div>
                      <div className="text-xs font-semibold text-[#1a1a1a] whitespace-nowrap overflow-hidden text-ellipsis">{notif.folderName || notif.projectName}</div>
                      {st === 'ok'
                        ? <span className="text-[11px] px-2 py-0.5 rounded inline-block mt-[3px] bg-[#eafbea] text-[#1e7e1e]">{String(tr.importNotifSuccess)}</span>
                        : st === 'err'
                          ? <span className="text-[11px] px-2 py-0.5 rounded inline-block mt-[3px] bg-[#fdecea] text-[#c0392b]">{String(tr.importNotifError)}</span>
                          : !notif.read && (
                            <div className="flex gap-[5px] mt-1 flex-wrap">
                              <button className="bg-[#e05a00] text-white border-none rounded px-2 py-[3px] text-[11px] font-semibold cursor-pointer hover:enabled:bg-[#c44f00] disabled:opacity-50 disabled:cursor-default"
                                disabled={st === 'loading'}
                                onClick={() => handleImportFromNotif(notif)}>
                                {st === 'loading' ? String(tr.importing) : (isFolder ? '⬇ Importer a la racine' : String(tr.importToHome))}
                              </button>
                              <button className="bg-[#f0f0f0] text-[#555] border-none rounded px-2 py-[3px] text-[11px] cursor-pointer hover:bg-[#e4e4e4]" onClick={() => handleMarkRead(notif)}>{String(tr.markRead)}</button>
                            </div>
                          )
                      }
                    </div>
                  </div>
                );
              })
          }
        </div>
      )}

      {/* Import from link modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black/30 z-[900] flex items-center justify-center" onClick={() => { setShowImport(false); setImportStatus(null); }}>
          <div className="bg-white rounded-lg shadow-lg px-6 pb-4.5 pt-[22px] w-[440px] flex flex-col gap-2.5" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="text-[15px] font-bold text-[#1a1a1a]">{String(tr.importTitle)}</div>
            <p className="text-xs text-[#666] -mt-1">{String(tr.importLinkLabel)}</p>
            <input
              className="w-full border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5]"
              value={importLink}
              autoFocus
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setImportLink(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') handleImportLink(); if (e.key === 'Escape') { setShowImport(false); setImportStatus(null); } }}
              placeholder={String(tr.importLinkPh)}
            />
            {importStatus && (
              <div className={`text-xs px-2 py-1.5 rounded ${importStatus.ok ? 'bg-[#eafbea] text-[#1e7e1e]' : 'bg-[#fdecea] text-[#c0392b]'}`}>
                {importStatus.msg}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-1">
              <button className="bg-[#eee] text-[#333] border-none rounded px-3.5 py-1.5 cursor-pointer text-[13px] hover:bg-[#e0e0e0]" onClick={() => { setShowImport(false); setImportStatus(null); setImportLink(''); }}>{String(tr.cancelBtn)}</button>
              <button className="bg-[#e05a00] text-white border-none rounded px-4 py-1.5 cursor-pointer text-[13px] font-semibold flex items-center gap-1.5 hover:bg-[#c44f00]" onClick={handleImportLink}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {String(tr.importBtn)}
              </button>
            </div>

            {/* Free tier: restore a full workbook snapshot */}
            {freeMode && onImportWorkbook && (
              <>
                <div className="flex items-center gap-2 my-1">
                  <div className="flex-1 h-px bg-[#e5e5e5]" />
                  <span className="text-[10px] text-[#aaa] uppercase tracking-wider">{lang === 'fr' ? 'ou' : 'or'}</span>
                  <div className="flex-1 h-px bg-[#e5e5e5]" />
                </div>
                <div className="text-[13px] font-semibold text-[#1a1a1a]">📦 {String(tr.importWorkbook)}</div>
                <p className="text-xs text-[#666] -mt-1">{String(tr.importWorkbookLabel)}</p>
                <label className="flex items-center justify-center gap-2 border border-dashed border-[#bbb] rounded px-3 py-3 cursor-pointer hover:border-[#7aacf5] hover:bg-[#f6faff] text-[12px] text-[#666]">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  {lang === 'fr' ? 'Sélectionner un fichier .json' : 'Pick a .json file'}
                  <input type="file" accept="application/json,.json" className="hidden"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setShowImport(false);
                      void onImportWorkbook(file);
                    }}
                  />
                </label>
              </>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar flex items-center px-1 py-1 border-b border-[#e0e0e0] bg-white gap-px">
        <button className="tool-btn border-none bg-transparent w-8 h-8 flex items-center justify-center cursor-pointer rounded text-[15px] text-[#444] hover:enabled:bg-[#e4e4e4] disabled:opacity-35 disabled:cursor-default" title={String(tr.newRootFolder)} onClick={(e: React.MouseEvent) => { e.stopPropagation(); startCreate(null, 'folder'); }}>🗂️</button>
        <button className="tool-btn border-none bg-transparent w-8 h-8 flex items-center justify-center cursor-pointer rounded text-[15px] text-[#444] hover:enabled:bg-[#e4e4e4] disabled:opacity-35 disabled:cursor-default" title={String(tr.newFolder)} onClick={(e: React.MouseEvent) => { e.stopPropagation(); startCreate(selected?.type === 'folder' ? selected.id : null, 'folder'); }}>📁+</button>
        <button className="tool-btn border-none bg-transparent w-8 h-8 flex items-center justify-center cursor-pointer rounded text-[15px] text-[#444] hover:enabled:bg-[#e4e4e4] disabled:opacity-35 disabled:cursor-default" title={String(tr.newProject)} onClick={(e: React.MouseEvent) => { e.stopPropagation(); startCreate(selected?.type === 'folder' ? selected.id : '__inbox__', 'project'); }}>💡+</button>
        <button className="tool-btn border-none bg-transparent w-8 h-8 flex items-center justify-center cursor-pointer rounded text-[15px] text-[#444] hover:enabled:bg-[#e4e4e4] disabled:opacity-35 disabled:cursor-default" title={String(tr.newWhiteboard)}
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); startCreate(selected?.type === 'folder' ? selected.id : '__inbox__', 'whiteboard'); }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2.5" ry="2.5"/>
            <path d="M6 15 Q9 11 12 14 Q15 17 18 12" strokeWidth="2.2"/>
            <line x1="15" y1="4" x2="19" y2="0.5"/>
            <rect x="17.5" y="-0.5" width="5" height="3" rx="0.7" transform="rotate(35 17.5 -0.5)"/>
          </svg>+
        </button>
        <button className="tool-btn border-none bg-transparent w-8 h-8 flex items-center justify-center cursor-pointer rounded text-[15px] text-[#444] hover:enabled:bg-[#e4e4e4] disabled:opacity-35 disabled:cursor-default" title={String(tr.newTextDoc)}
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); startCreate(selected?.type === 'folder' ? selected.id : '__inbox__', 'textdoc'); }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="8" y1="13" x2="16" y2="13"/>
            <line x1="8" y1="17" x2="16" y2="17"/>
            <line x1="8" y1="9" x2="11" y2="9"/>
          </svg>+
        </button>
        {/* Import from link — right-aligned (autosave replaces the manual Save button) */}
        <button className="tool-btn border-none bg-transparent w-8 h-8 flex items-center justify-center cursor-pointer rounded text-[#444] hover:enabled:bg-[#e4e4e4] disabled:opacity-35 disabled:cursor-default ml-auto" title={String(tr.importBtn)}
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); setShowImport((v: boolean) => !v); setShowNotifs(false); setShowExport(false); }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
        {/* Export */}
        <div className="tool-btn-wrap relative" ref={exportBtnRef}>
          <button
            className="tool-btn border-none bg-transparent w-8 h-8 flex items-center justify-center cursor-pointer rounded text-[#444] hover:enabled:bg-[#e4e4e4] disabled:opacity-35 disabled:cursor-default"
            title={String(tr.exportBtn)}
            disabled={!currentProject?.id && !freeMode}
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); setShowExport((v: boolean) => !v); setShowShare(false); }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v13"/>
              <polyline points="7 8 12 3 17 8"/>
              <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3"/>
            </svg>
          </button>
          {showExport && (
            <div className={`export-dropdown absolute top-[calc(100%+4px)] ${freeMode ? 'right-0' : 'left-0'} bg-white border border-[#ddd] rounded-md shadow-lg z-[500] min-w-[240px] py-1`} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              {currentProject?.id && ([['svg', '🖼', String(tr.exportSvg), String(tr.exportSvgHint)],
                ['png', '📷', String(tr.exportPng), String(tr.exportPngHint)],
                ['pdf', '📄', String(tr.exportPdf), String(tr.exportPdfHint)]] as [string, string, string, string][]).map(([fmt, ico, lbl, hint]) => (
                <div key={fmt} className="flex flex-col px-3.5 py-2 cursor-pointer text-[13px] gap-[2px] hover:bg-[#f4f4f4]" onClick={() => handleExport(fmt)}>
                  <span className="font-medium text-[#222]">{ico} {lbl}</span>
                  <span className="text-[11px] text-[#888]">{hint}</span>
                </div>
              ))}
              {freeMode && onExportWorkbook && (
                <>
                  {currentProject?.id && <div className="h-px bg-[#eee] mx-2 my-1" />}
                  <div className="flex flex-col px-3.5 py-2 cursor-pointer text-[13px] gap-[2px] hover:bg-[#f4f4f4]"
                       onClick={() => { setShowExport(false); void onExportWorkbook(); }}>
                    <span className="font-medium text-[#222]">📦 {String(tr.exportWorkbook)}</span>
                    <span className="text-[11px] text-[#888]">{String(tr.exportWorkbookHint)}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {/* Share — not available in free tier (no server, no collab) */}
        {!freeMode && (
          <button
            className="tool-btn border-none bg-transparent w-8 h-8 flex items-center justify-center cursor-pointer rounded text-[#444] hover:enabled:bg-[#e4e4e4] disabled:opacity-35 disabled:cursor-default" title={String(tr.shareBtn)} disabled={!currentProject?.id}
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); setShowShare(true); setShowExport(false); setShareStatus(null); setShareTarget(''); }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </button>
        )}
      </div>

      {/* Tree */}
      <div className="tree flex-1 overflow-y-auto overflow-x-hidden py-1" onContextMenu={(e: React.MouseEvent) => e.preventDefault()}>
        {/* Inbox */}
        <div
          className={`tree-row flex items-center gap-1.5 py-0.5 pl-2 pr-1.5 cursor-pointer rounded-sm mx-1 my-px min-h-[26px] text-[13px] hover:bg-[#e4e4e4] ${selected?.id === '__inbox__' ? 'bg-[#cde0f7]' : ''} ${inboxDropOver ? 'bg-[#d0e8ff] outline-2 outline-dashed outline-[#5588cc]' : ''}`}
          onClick={() => setSelected({ id: '__inbox__', type: 'folder', name: String(tr.home) })}
          onContextMenu={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); openCtx(e, { id: '__inbox__', type: 'folder', name: String(tr.home) }); }}
          onDragOver={(e: React.DragEvent) => { e.preventDefault(); setInboxDropOver(true); }}
          onDragLeave={() => setInboxDropOver(false)}
          onDrop={(e: React.DragEvent) => { setInboxDropOver(false); const id = e.dataTransfer.getData('text/plain'); if (id) handleProjectDrop(id, null); }}
        >
          <span className="w-4 shrink-0 flex items-center justify-center cursor-pointer" onClick={(e: React.MouseEvent) => { e.stopPropagation(); toggle('__inbox__'); }}>
            <DisclosureTriangle open={!!expanded['__inbox__']} />
          </span>
          <span className="w-4 shrink-0 text-center text-[13px] leading-none">📥</span>
          <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{String(tr.home)}</span>
          <span className="text-[#5588cc] text-[11px] shrink-0">({sortedRootProjects.length})</span>
        </div>

        {expanded['__inbox__'] && (
          <>
            {inlineInput.show && inlineInput.parentId === '__inbox__' && (
              <div className="flex items-center pr-1.5 py-0.5 mx-1 my-px" style={{ paddingLeft: 24 }}>
                <input autoFocus value={inlineInput.value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInlineInput((i: InlineInputState) => ({ ...i, value: e.target.value }))}
                  onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); confirmInline(); } if (e.key === 'Escape') cancelInline(); }}
                  onBlur={confirmInline} className="flex-1 border border-[#7aacf5] rounded-sm px-1.5 py-0.5 text-xs outline-none bg-white"
                  placeholder={String(inlineInput.type === 'folder' ? tr.folderPlaceholder : tr.projectPlaceholder)} />
              </div>
            )}
            {sortedRootProjects.map((p: any) => (
              <ProjectRow key={p.id} project={p} depth={1} selected={selected}
                onSelect={setSelected} onOpen={onOpenProject} onCtx={openCtx} />
            ))}
          </>
        )}

        {/* Root-level folder inline input */}
        {inlineInput.show && inlineInput.parentId === null && inlineInput.type === 'folder' && (
          <div className="flex items-center pl-2 pr-1.5 py-0.5 mx-1 my-px">
            <input autoFocus value={inlineInput.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInlineInput((i: InlineInputState) => ({ ...i, value: e.target.value }))}
              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); confirmInline(); } if (e.key === 'Escape') cancelInline(); }}
              onBlur={confirmInline} className="flex-1 border border-[#7aacf5] rounded-sm px-1.5 py-0.5 text-xs outline-none bg-white" placeholder={String(tr.folderPlaceholder)} />
          </div>
        )}

        {sortedRootFolders.map((folder: any) => (
          <FolderRow key={folder.id} folder={folder} depth={0}
            expanded={expanded} selected={selected} allFolders={folders} allProjects={projects}
            inlineInput={inlineInput} onToggle={toggle} onSelect={setSelected}
            onOpen={onOpenProject} onCtx={openCtx} onProjectDrop={handleProjectDrop}
            inlineValue={inlineInput.value}
            onInlineValueChange={(v: string) => setInlineInput((i: InlineInputState) => ({ ...i, value: v }))}
            onConfirmInline={confirmInline} onCancelInline={cancelInline}
          />
        ))}
      </div>

      {/* Rename modal */}
      {renameState.show && (
        <div className="rename-modal absolute bottom-3 left-3 right-3 bg-white border border-[#ddd] rounded-md shadow-md p-3 z-[100]" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <div className="text-xs text-[#666] mb-1.5">{String(tr.renameTitle)}</div>
          <input ref={renameInputRef} value={renameState.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRenameState((s: RenameState) => ({ ...s, value: e.target.value }))}
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); confirmRename(); } if (e.key === 'Escape') setRenameState({ show: false, target: null, value: '' }); }}
            className="w-full border border-[#7aacf5] rounded px-2 py-1.5 text-[13px] outline-none" />
          <div className="flex justify-end gap-1.5 mt-2">
            <button className="bg-[#e05a00] text-white border-none rounded px-3 py-1 cursor-pointer text-xs" onClick={confirmRename}>{String(tr.ok)}</button>
            <button className="bg-[#eee] text-[#333] border-none rounded px-3 py-1 cursor-pointer text-xs" onClick={() => setRenameState({ show: false, target: null, value: '' })}>{String(tr.cancel)}</button>
          </div>
        </div>
      )}

      {/* Share modal */}
      {showShare && (
        <div className="fixed inset-0 bg-black/30 z-[900] flex items-center justify-center" onClick={() => { setShowShare(false); setShareStatus(null); setShareSuggestions([]); }}>
          <div className="share-modal bg-white rounded-lg shadow-lg px-6 pb-4.5 pt-[22px] w-[420px] flex flex-col gap-2.5" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="text-[15px] font-bold text-[#1a1a1a]">{String(tr.shareTitle)}</div>

            {/* Recipient */}
            <p className="text-xs text-[#666] -mt-1">{String(tr.shareWith)}</p>
            <div className="relative">
              <input
                className="w-full border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5]"
                value={shareTarget}
                autoFocus
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onShareTargetChange(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') handleShare(); if (e.key === 'Escape') { setShowShare(false); setShareStatus(null); } }}
                placeholder={String(tr.sharePlaceholder)}
              />
              {shareSuggestions.length > 0 && (
                <div className="absolute top-[calc(100%+3px)] left-0 right-0 bg-white border border-[#ddd] rounded-md shadow-lg z-[200] overflow-hidden">
                  {shareSuggestions.map((m: any) => (
                    <div key={m._id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer border-b border-[#f4f4f4] last:border-b-0 hover:bg-[#f7faff]"
                      onClick={() => { setShareTarget(m.email); setShareSuggestions([]); }}>
                      <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0" style={{ background: m.avatarColor || '#29ABE2' }}>
                        {`${(m.firstName||'')[0]||''}${(m.lastName||'')[0]||''}`.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="text-[13px] font-semibold text-[#1a1a1a]">{`${m.firstName||''} ${m.lastName||''}`.trim()}</div>
                        <div className="text-[11px] text-[#888]">{m.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Format */}
            <p className="text-xs text-[#666] -mt-1" style={{ marginTop: 14 }}>{String(tr.shareFormat)}</p>
            <div className="flex gap-2 flex-wrap mb-1">
              {([
                { fmt: 'pdf', icon: '📄', label: 'PDF' },
                { fmt: 'svg', icon: '🖼', label: 'SVG' },
                { fmt: 'png', icon: '📷', label: 'PNG' },
              ] as { fmt: string; icon: string; label: string }[]).map(({ fmt, icon, label }) => (
                <button key={fmt}
                  className={`share-fmt-btn flex flex-col items-center gap-1 border border-[#e0e0e0] bg-[#fafafa] rounded-lg px-3.5 py-2.5 cursor-pointer text-xs text-[#444] flex-1 min-w-[70px] transition-colors duration-[130ms] hover:bg-[#f0f7ff] hover:border-[#29ABE2] ${shareFormat === fmt ? '!border-[#29ABE2] !bg-[#e8f6fd] !text-[#0d7ab5] font-semibold' : ''}`}
                  onClick={() => setShareFormat(fmt)}>
                  <span className="text-lg">{icon}</span>
                  <span>{label}</span>
                </button>
              ))}
              <button
                className={`share-fmt-btn flex flex-row justify-center gap-2 px-3.5 py-2.5 cursor-pointer text-xs text-[#444] flex-basis-full border border-[#e0e0e0] bg-[#fafafa] rounded-lg transition-colors duration-[130ms] hover:bg-[#f0f7ff] hover:border-[#29ABE2] ${shareFormat === null ? '!border-[#29ABE2] !bg-[#e8f6fd] !text-[#0d7ab5] font-semibold' : ''}`}
                onClick={() => setShareFormat(null)}>
                <span className="text-lg">🔗</span>
                <span>{String(tr.shareAccessOnly)}</span>
              </button>
            </div>

            {shareStatus && (
              <div className={`text-xs px-2 py-1.5 rounded ${shareStatus.ok ? 'bg-[#eafbea] text-[#1e7e1e]' : 'bg-[#fdecea] text-[#c0392b]'}`}>
                {shareStatus.msg}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-1">
              <button className="bg-[#eee] text-[#333] border-none rounded px-3.5 py-1.5 cursor-pointer text-[13px] hover:bg-[#e0e0e0]" onClick={() => { setShowShare(false); setShareStatus(null); setShareTarget(''); setShareSuggestions([]); }}>{String(tr.cancelBtn)}</button>
              {shareFormat && (
                <button className="bg-white text-[#444] border border-[#d0d0d0] rounded px-3.5 py-1.5 cursor-pointer text-[13px] flex items-center gap-1.5 whitespace-nowrap hover:bg-[#f4f4f4] hover:border-[#aaa]" onClick={() => handleExport(shareFormat)}>
                  {lang === 'fr' ? '💾 Sauvegarder localement' : '💾 Save locally'}
                </button>
              )}
              <button className="bg-[#e05a00] text-white border-none rounded px-4 py-1.5 cursor-pointer text-[13px] font-semibold flex items-center gap-1.5 hover:bg-[#c44f00] disabled:opacity-50 disabled:cursor-default" disabled={shareSending || !shareTarget.trim()} onClick={handleShare}>
                {shareSending ? String(tr.shareSending) : String(tr.shareSendBtn)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Members panel */}
      {showMembers && (() => {
        const tabs = [
          { key: 'members', label: String(tr.membersTabMembers), filter: (m: any) => m.type !== 'guest' && !m.pending },
          { key: 'guest1', label: String(tr.membersTabGuests1), filter: (m: any) => m.type === 'guest' && (m.projects?.length ?? 0) <= 1 },
          { key: 'guestN', label: String(tr.membersTabGuestsN), filter: (m: any) => m.type === 'guest' && (m.projects?.length ?? 0) > 1 },
          { key: 'pending', label: String(tr.membersTabPending), filter: (m: any) => !!m.pending },
        ];
        const activeMembers = members.filter(tabs.find(t => t.key === memberTab)?.filter ?? (() => true));
        const filtered = memberFilter
          ? activeMembers.filter((m: any) => `${m.firstName} ${m.lastName} ${m.email}`.toLowerCase().includes(memberFilter.toLowerCase()))
          : activeMembers;
        const initials = (m: any): string => `${(m.firstName||'')[0]||''}${(m.lastName||'')[0]||''}`.toUpperCase() || (m.email||'?')[0].toUpperCase();
        const formatDate = (d: string | undefined): string => d ? new Date(d).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { month: 'short', year: 'numeric' }) : '—';

        return (
          <div className="fixed inset-0 bg-black/30 z-[900] flex items-center justify-center" onClick={() => { setShowMembers(false); setMemberStatus(null); }}>
            <div className="members-modal bg-white rounded-xl shadow-lg w-[680px] max-w-[96vw] max-h-[88vh] flex flex-col overflow-hidden" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between pt-5 px-6">
                <div className="text-lg font-bold text-[#1a1a1a]">
                  {typeof tr.membersTitle === 'function' ? tr.membersTitle(members.length, null) : String(tr.membersTitle)}
                </div>
                <button className="border-none bg-none text-base text-[#888] cursor-pointer px-2 py-1 rounded hover:bg-[#f0f0f0] hover:text-[#333]" onClick={() => setShowMembers(false)}>✕</button>
              </div>

              {/* Tabs */}
              <div className="members-tabs flex gap-0 pt-3 px-6 border-b border-[#e8e8e8] overflow-x-auto">
                {tabs.map(t => (
                  <button key={t.key}
                    className={`border-none bg-none px-3.5 py-2 text-[13px] cursor-pointer whitespace-nowrap border-b-2 border-transparent -mb-px hover:text-[#333] ${memberTab === t.key ? 'text-[#29ABE2] border-b-[#29ABE2] font-semibold' : 'text-[#666]'}`}
                    onClick={() => setMemberTab(t.key)}>
                    {t.label} ({members.filter(t.filter).length})
                  </button>
                ))}
              </div>

              {/* Invite row */}
              <div className="px-6 pt-3.5 pb-1">
                <button className="bg-[#29ABE2] text-white border-none rounded-md px-4 py-2 text-[13px] font-semibold cursor-pointer hover:bg-[#1a90c8]" onClick={() => {}}>
                  {String(tr.membersInviteBtn)}
                </button>
              </div>

              {/* Invite form */}
              <div className="flex gap-2 px-6 py-2 items-center flex-wrap">
                <input className="flex-1 border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5]"
                  placeholder={String(tr.membersInviteEmail)}
                  value={memberInvite.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMemberInvite((f: { email: string; role: string }) => ({ ...f, email: e.target.value }))}
                  onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') handleInviteMember(); }}
                />
                <select className="border border-[#bbb] rounded px-2 py-1.5 text-xs bg-white text-[#333] cursor-pointer outline-none" value={memberInvite.role}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setMemberInvite((f: { email: string; role: string }) => ({ ...f, role: e.target.value }))}>
                  <option value="member">{String(tr.membersRoleMember)}</option>
                  <option value="guest">{String(tr.membersRoleGuest)}</option>
                  <option value="admin">{String(tr.membersRoleAdmin)}</option>
                </select>
                <button className="bg-[#e05a00] text-white border-none rounded px-4 py-1.5 cursor-pointer text-[13px] font-semibold flex items-center gap-1.5 hover:bg-[#c44f00]" onClick={handleInviteMember}>{String(tr.membersSend)}</button>
              </div>

              {memberStatus && (
                <div className={`text-xs px-2 py-1.5 rounded ${memberStatus.ok ? 'bg-[#eafbea] text-[#1e7e1e]' : 'bg-[#fdecea] text-[#c0392b]'}`} style={{ margin: '0 0 8px' }}>
                  {memberStatus.msg}
                </div>
              )}

              {/* Filter */}
              <input className="border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5] mx-6 mb-2 w-[calc(100%-48px)] box-border"
                placeholder={String(tr.membersFilter)}
                value={memberFilter}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMemberFilter(e.target.value)} />

              {/* Members list */}
              <div className="flex-1 overflow-y-auto px-6 pb-4 flex flex-col gap-[2px]">
                {filtered.length === 0
                  ? <div className="text-xs text-[#aaa] italic px-3 py-3.5 text-center">{String(tr.membersNone)}</div>
                  : filtered.map((m: any) => (
                    <div key={m._id} className="flex items-center gap-3 py-2.5 border-b border-[#f4f4f4]">
                      {/* Avatar */}
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[13px] font-bold shrink-0" style={{ background: m.avatarColor || '#29ABE2' }}>
                        {initials(m)}
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-[#1a1a1a]">
                          {`${m.firstName||''} ${m.lastName||''}`.trim() || '—'}
                          {m.username && <span className="font-normal text-[#888] text-xs"> @{m.username}</span>}
                        </div>
                        <div className="text-[11px] text-[#aaa] mt-px">{typeof tr.membersLastSeen === 'function' ? tr.membersLastSeen(formatDate(m.lastActivity)) : String(tr.membersLastSeen)}</div>
                      </div>
                      {/* Role selector */}
                      <select className="border border-[#ddd] rounded px-1 py-[3px] text-[11px] bg-white cursor-pointer outline-none" value={m.role || 'member'}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleMemberRole(m, e.target.value)}>
                        <option value="member">{String(tr.membersRoleMember)}</option>
                        <option value="guest">{String(tr.membersRoleGuest)}</option>
                        <option value="admin">{String(tr.membersRoleAdmin)}</option>
                      </select>
                      {/* Remove */}
                      <button className="border border-[#ddd] bg-[#f8f8f8] text-[#c0392b] rounded px-2 py-[3px] text-[10px] cursor-pointer whitespace-nowrap hover:!bg-[#fff0ee] hover:!border-[#f5c6c0]"
                        onClick={() => handleRemoveMember(m)}>
                        {String(tr.membersRemove)}
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Admin panel */}
      {showAdmin && (
        <div className="fixed inset-0 bg-black/30 z-[900] flex items-center justify-center" onClick={() => { setShowAdmin(false); setAdminStatus(null); }}>
          <div className="admin-modal bg-white rounded-xl shadow-lg px-6 pb-5 pt-6 w-[680px] max-w-[96vw] max-h-[88vh] overflow-y-auto flex flex-col gap-3.5" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="text-lg font-bold text-[#1a1a1a]">{String(tr.adminTitle)}</div>

            {/* Invite row */}
            <div className="flex gap-2 items-center flex-wrap">
              <input className="flex-1 border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5]"
                placeholder={String(tr.adminInviteEmail)}
                value={inviteForm.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteForm((f: { email: string; role: string }) => ({ ...f, email: e.target.value }))}
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') handleInvite(); }}
              />
              <select className="border border-[#bbb] rounded px-2 py-1.5 text-xs bg-white text-[#333] cursor-pointer outline-none" value={inviteForm.role}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setInviteForm((f: { email: string; role: string }) => ({ ...f, role: e.target.value }))}>
                <option value="user">{String(tr.adminRoleUser)}</option>
                <option value="admin">{String(tr.adminRoleAdmin)}</option>
              </select>
              <button className="bg-[#e05a00] text-white border-none rounded px-4 py-1.5 cursor-pointer text-[13px] font-semibold flex items-center gap-1.5 hover:bg-[#c44f00]" onClick={handleInvite}>{String(tr.adminInviteBtn)}</button>
            </div>

            {adminStatus && (
              <div className={`text-xs px-2 py-1.5 rounded ${adminStatus.ok ? 'bg-[#eafbea] text-[#1e7e1e]' : 'bg-[#fdecea] text-[#c0392b]'}`}>
                {adminStatus.msg}
              </div>
            )}

            {/* Users table */}
            <div className="border border-[#ebebeb] rounded-md overflow-hidden">
              <div className="admin-table-head grid grid-cols-[1.4fr_2fr_1fr_0.8fr_1.4fr] bg-[#f7f7f7] px-3 py-1.5 text-[10px] font-bold text-[#888] uppercase tracking-wide gap-2">
                <div>{String(tr.adminName)}</div>
                <div>{String(tr.adminEmail)}</div>
                <div>{String(tr.adminRole)}</div>
                <div>{String(tr.adminStatus)}</div>
                <div>{String(tr.adminActions)}</div>
              </div>
              {orgUsers.map((u: any) => (
                <div key={u._id} className={`admin-table-row grid grid-cols-[1.4fr_2fr_1fr_0.8fr_1.4fr] px-3 py-2 gap-2 items-center border-t border-[#f2f2f2] text-xs transition-colors duration-100 hover:bg-[#fafafa] ${u.active === false ? 'opacity-50' : ''}`}>
                  <span className="font-semibold text-[#222]">
                    {`${u.firstName || ''} ${u.lastName || ''}`.trim() || '—'}
                  </span>
                  <span className="text-[#555] text-[11px] break-all">{u.email}</span>
                  <span>
                    <select className="border border-[#ddd] rounded px-1 py-[3px] text-[11px] bg-white cursor-pointer outline-none"
                      value={u.role || 'user'}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChangeAccess(u, e.target.value)}>
                      <option value="user">{String(tr.adminRoleUser)}</option>
                      <option value="admin">{String(tr.adminRoleAdmin)}</option>
                    </select>
                  </span>
                  <span>
                    <button className={`inline-block px-2 py-0.5 rounded-[10px] text-[10px] font-semibold cursor-pointer ${u.active !== false ? 'bg-[#eafbea] text-[#1a7a1a]' : 'bg-[#f0f0f0] text-[#888]'}`} onClick={() => handleToggleUser(u)}>
                      {u.active !== false ? String(tr.adminActive) : String(tr.adminInactive)}
                    </button>
                  </span>
                  <span className="flex gap-[5px] flex-wrap">
                    <button className="border border-[#ddd] bg-[#f8f8f8] text-[#444] rounded px-2 py-[3px] text-[10px] cursor-pointer whitespace-nowrap hover:bg-[#efefef]"
                      onClick={() => handleRemoveUser(u)}>
                      {String(tr.adminRemove)}
                    </button>
                  </span>
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-2">
              <button className="bg-[#eee] text-[#333] border-none rounded px-3.5 py-1.5 cursor-pointer text-[13px] hover:bg-[#e0e0e0]" onClick={() => setShowAdmin(false)}>{String(tr.close)}</button>
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/30 z-[900] flex items-center justify-center" onClick={() => { setShowSettings(false); setProfileStatus(null); setPwdStatus(null); }}>
          <div className="settings-modal bg-white rounded-xl shadow-lg px-6 pb-5 pt-6 w-[400px] max-h-[90vh] overflow-y-auto flex flex-col" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="text-base font-bold text-[#1a1a1a] mb-4.5">{String(tr.settingsTitle)}</div>

            {/* Profile */}
            <div className="flex flex-col gap-2.5">
              <div className="text-xs font-bold text-[#888] uppercase tracking-wide mb-1">{String(tr.profileSection)}</div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-[#666]">{String(tr.firstName)}</label>
                <input className="w-full border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5]"
                  value={profile.firstName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProfile((p: Profile) => ({ ...p, firstName: e.target.value }))}
                  placeholder={String(tr.firstName)} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-[#666]">{String(tr.lastName)}</label>
                <input className="w-full border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5]"
                  value={profile.lastName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProfile((p: Profile) => ({ ...p, lastName: e.target.value }))}
                  placeholder={String(tr.lastName)} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-[#666]">{String(tr.organisation)}</label>
                <input className="w-full border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5]"
                  value={profile.organisation}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProfile((p: Profile) => ({ ...p, organisation: e.target.value }))}
                  placeholder={String(tr.organisation)} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-[#666]">{String(tr.email)}</label>
                <input className="w-full border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5]"
                  type="email"
                  value={profile.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProfile((p: Profile) => ({ ...p, email: e.target.value }))}
                  placeholder={String(tr.email)} />
              </div>
              {profileStatus && (
                <div className={`text-xs px-2 py-1.5 rounded ${profileStatus.ok ? 'bg-[#eafbea] text-[#1e7e1e]' : 'bg-[#fdecea] text-[#c0392b]'}`}>{profileStatus.msg}</div>
              )}
              <div className="flex justify-end gap-2 mt-1">
                <button className="bg-[#e05a00] text-white border-none rounded px-4 py-1.5 cursor-pointer text-[13px] font-semibold flex items-center gap-1.5 hover:bg-[#c44f00]" onClick={handleSaveProfile}>{String(tr.saveProfile)}</button>
              </div>
            </div>

            {/* Password */}
            <div className="h-px bg-[#efefef] my-4.5" />

            <div className="flex flex-col gap-2.5">
              <div className="text-xs font-bold text-[#888] uppercase tracking-wide mb-1">{String(tr.passwordSection)}</div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-[#666]">{String(tr.currentPassword)}</label>
                <input className="w-full border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5]"
                  type="password"
                  value={pwdFields.old}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPwdFields((p: { old: string; new: string; confirm: string }) => ({ ...p, old: e.target.value }))}
                  placeholder={String(tr.currentPassword)} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-[#666]">{String(tr.newPassword)}</label>
                <input className="w-full border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5]"
                  type="password"
                  value={pwdFields.new}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPwdFields((p: { old: string; new: string; confirm: string }) => ({ ...p, new: e.target.value }))}
                  placeholder={String(tr.newPassword)} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-[#666]">{String(tr.confirmPassword)}</label>
                <input className="w-full border border-[#bbb] rounded px-2.5 py-[7px] text-[13px] outline-none transition-colors duration-150 focus:border-[#7aacf5]"
                  type="password"
                  value={pwdFields.confirm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPwdFields((p: { old: string; new: string; confirm: string }) => ({ ...p, confirm: e.target.value }))}
                  placeholder={String(tr.confirmPassword)} />
              </div>
              {pwdStatus && (
                <div className={`text-xs px-2 py-1.5 rounded ${pwdStatus.ok ? 'bg-[#eafbea] text-[#1e7e1e]' : 'bg-[#fdecea] text-[#c0392b]'}`}>{pwdStatus.msg}</div>
              )}
              <div className="flex justify-end gap-2 mt-1">
                <button className="bg-[#e05a00] text-white border-none rounded px-4 py-1.5 cursor-pointer text-[13px] font-semibold flex items-center gap-1.5 hover:bg-[#c44f00]" onClick={handleChangePassword}>{String(tr.changePassword)}</button>
              </div>
            </div>

            <div className="h-px bg-[#efefef] my-4.5" />

            <div className="flex justify-end">
              <button className="bg-[#eee] text-[#333] border-none rounded px-3.5 py-1.5 cursor-pointer text-[13px] hover:bg-[#e0e0e0]" onClick={() => setShowSettings(false)}>{String(tr.close)}</button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade modal (free tier only) — pricing offers, fully i18n-driven */}
      {showUpgrade && (
        <div className="fixed inset-0 bg-black/40 z-[900] flex items-center justify-center p-4" onClick={() => setShowUpgrade(false)}>
          <div className="bg-white rounded-[14px] shadow-xl w-[960px] max-w-full max-h-[90vh] overflow-y-auto flex flex-col" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-8 pt-7 pb-4 text-center border-b border-[#f0f0f0]">
              <div className="text-[22px] font-bold text-[#1a1a1a] mb-1">{String(tr.upgradeTitle)}</div>
              <p className="text-[13px] text-[#666] max-w-[600px] mx-auto">{String(tr.upgradeSubtitle)}</p>
            </div>

            {/* Social impact banner */}
            <div className="px-8 py-3 bg-gradient-to-r from-[#eaf7ee] to-[#f1faf3] border-b border-[#dcefe1]">
              <p className="text-[12.5px] text-[#1e6b3a] leading-relaxed text-center max-w-[760px] mx-auto">
                <span className="mr-1.5">🌱</span>
                <strong className="font-bold">{lang === 'fr' ? '25 % de nos revenus' : lang === 'es' ? 'El 25 % de nuestros ingresos' : '25% of our revenue'}</strong>
                {lang === 'fr'
                  ? ' sont reversés à des ONG, associations et éducateurs pour initier les enfants à l’entrepreneuriat et les aider à résoudre par eux-mêmes leurs problématiques d’ingénierie et environnementales.'
                  : lang === 'es'
                    ? ' se destina a ONG, asociaciones y educadores para iniciar a los niños en el emprendimiento y ayudarles a resolver por sí mismos sus desafíos de ingeniería y medioambientales.'
                    : ' is donated to NGOs, associations and educators to introduce children to entrepreneurship and help them solve their own engineering and environmental challenges.'}
              </p>
            </div>

            {/* Tiers */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-6">
              {([
                { key: 'school', name: String(tr.tierSchoolName), price: 10,   seats: String(tr.upgradeSeatsUnlimited), highlight: false, desc: String(tr.tierSchoolDesc), cta: String(tr.ctaContactUs) },
                { key: 'indep',  name: String(tr.tierIndepName),  price: 50,   seats: String(tr.upgradeSeats1to2),      highlight: true,  desc: String(tr.tierIndepDesc),  cta: String(tr.ctaSubscribe) },
                { key: 'tpe',    name: String(tr.tierTpeName),    price: 40,   seats: String(tr.upgradeSeats3to10),     highlight: false, desc: String(tr.tierTpeDesc),    cta: String(tr.ctaSubscribe) },
                { key: 'large',  name: String(tr.tierLargeName),  price: null, seats: String(tr.upgradeSeatsUnlimited), highlight: false, desc: String(tr.tierLargeDesc),  cta: String(tr.ctaRequestQuote) },
              ] as { key: string; name: string; price: number | null; seats: string; highlight: boolean; desc: string; cta: string }[]).map(tier => (
                <div key={tier.key}
                  className={`flex flex-col rounded-xl border transition-all ${tier.highlight ? 'border-[#e05a00] bg-gradient-to-br from-[#fff5ec] to-white shadow-md relative' : 'border-[#e5e5e5] bg-white hover:shadow-sm'}`}
                  style={{ padding: 18 }}>
                  {tier.highlight && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#e05a00] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full">
                      {String(tr.upgradePopular)}
                    </div>
                  )}
                  <div className="text-[15px] font-bold text-[#1a1a1a] mb-2">{tier.name}</div>
                  <div className="flex items-baseline gap-1 mb-1">
                    {tier.price !== null ? (
                      <>
                        <span className="text-[28px] font-bold text-[#1a1a1a] leading-none">{tier.price}€</span>
                        <span className="text-[11px] text-[#888]">{String(tr.upgradePerUserYear)}</span>
                      </>
                    ) : (
                      <span className="text-[22px] font-bold text-[#1a1a1a] leading-none">{String(tr.upgradeCustomQuote)}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#888] mb-3 font-medium">{tier.seats}</div>
                  <p className="text-[12px] text-[#555] leading-relaxed mb-4 flex-1">{tier.desc}</p>
                  <a href={`mailto:contact@ipfluencer.com?subject=${encodeURIComponent(`Inno-CK — ${tier.name}`)}&body=${encodeURIComponent(`Bonjour,\n\nJe souhaite en savoir plus sur l'offre « ${tier.name} » d'Intelligence Collective.\n\nMerci !`)}`}
                    className={`text-center text-[12px] font-semibold rounded-md py-2 transition-colors ${tier.highlight ? 'bg-[#e05a00] text-white hover:bg-[#c44f00]' : 'bg-[#f3f3f3] text-[#1a1a1a] hover:bg-[#e8e8e8]'}`}>
                    {tier.cta}
                  </a>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-8 py-4 border-t border-[#f0f0f0] flex items-center justify-between gap-3">
              <span className="text-[11px] text-[#888]">{String(tr.upgradeFooter)}</span>
              <button onClick={() => setShowUpgrade(false)}
                className="text-[12px] text-[#666] hover:text-[#1a1a1a] px-3 py-1.5 rounded hover:bg-[#f0f0f0]">
                {String(tr.upgradeClose)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* About modal */}
      {showAbout && (
        <div className="fixed inset-0 bg-black/30 z-[900] flex items-center justify-center" onClick={() => setShowAbout(false)}>
          <div className="about-modal bg-white rounded-[14px] shadow-lg px-7 pb-6 pt-7 w-[400px] max-w-[94vw] flex flex-col gap-3.5 text-center" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="flex justify-center">
              <img src="/logo2_enjeu-org.png" alt="enjeu.org" className="w-[200px] h-auto object-contain" />
            </div>
            <div className="text-base font-bold text-[#1a1a1a]">{String(tr.aboutTitle)}</div>
            <p className="text-[13px] text-[#555] leading-relaxed text-left">
              {(() => {
                const body = String(tr.aboutBody);
                const brand = 'ESSONE GROUP';
                const brandLink = (
                  <a href="https://www.essone.net/" target="_blank" rel="noopener noreferrer" className="text-[#29ABE2] font-semibold hover:underline">{brand}</a>
                );
                const marker = 'CertifAfrica.';
                const i = body.indexOf(marker);
                const withBreak = i === -1
                  ? [body]
                  : [body.slice(0, i + marker.length), <br key="br" />, body.slice(i + marker.length).trimStart()];
                return withBreak.map((seg, idx) => {
                  if (typeof seg !== 'string') return seg;
                  const bi = seg.indexOf(brand);
                  if (bi === -1) return <span key={idx}>{seg}</span>;
                  return <span key={idx}>{seg.slice(0, bi)}{brandLink}{seg.slice(bi + brand.length)}</span>;
                });
              })()}
            </p>
            <div className="h-px bg-gradient-to-r from-transparent via-[#29ABE2] to-transparent" />
            <div className="text-[15px] font-bold italic text-[#29ABE2] tracking-wide">{String(tr.aboutSlogan)}</div>
            <div className="text-[12px] text-[#666]">
              {lang === 'fr' ? 'Contact : ' : lang === 'es' ? 'Contacto: ' : 'Contact: '}
              <a href="mailto:contact@enjeu.org" className="text-[#29ABE2] font-semibold hover:underline">contact@enjeu.org</a>
            </div>
            <button className="bg-[#e05a00] text-white border-none rounded px-4 py-1.5 cursor-pointer text-[13px] font-semibold flex items-center gap-1.5 hover:bg-[#c44f00] self-center mt-2" style={{ alignSelf: 'center', marginTop: 8 }} onClick={() => setShowAbout(false)}>{String(tr.ok)}</button>
          </div>
        </div>
      )}

      {/* App name - visible only in collapsed mode */}
      <div className="collapsed-appname hidden" aria-hidden="true">
        enjeu.org
      </div>

      {/* Logout button - fixed at bottom */}
      <div className="logout-zone border-t border-[#e8e8e8] px-3 py-2.5 bg-white shrink-0 mt-auto flex flex-col gap-1.5">
        {/* enjeu.org vertical logo — collapsed mode only, above IPfluencer */}
        {collapsed && (
          <a href="https://enjeu.org" target="_blank" rel="noopener noreferrer"
            onClick={(e: React.MouseEvent) => e.stopPropagation()} title="enjeu.org">
            <img
              src="/logo_vertical_enjeu-org.png"
              alt="enjeu.org"
              className="logout-zone-logo block w-[120px] h-auto rounded opacity-90 mb-1.5"
            />
          </a>
        )}
        {/* Partner logos — IPfluencer + CertifAfrica (horizontal side-by-side in expanded, stacked vertical in collapsed) */}
        {collapsed ? (
          <>
            <a href="https://www.ipfluencer.com" target="_blank" rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()} title="IPfluencer">
              <img
                src="/logo_ipfluencer_vertical.png"
                alt="IPfluencer"
                className="logout-zone-logo block w-[120px] h-auto rounded opacity-90 mb-1.5"
              />
            </a>
            <a href="https://www.certifafrica.com/" target="_blank" rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()} title="CertifAfrica">
              <img
                src="/logo_certifafrica_vertical.png"
                alt="CertifAfrica"
                className="logout-zone-logo certifafrica-collapsed-logo block w-[120px] h-auto rounded opacity-90 mb-1.5"
              />
            </a>
          </>
        ) : (
          <div className="flex flex-row items-center justify-center gap-2 mb-1.5">
            <a href="https://www.ipfluencer.com" target="_blank" rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()} title="IPfluencer" className="shrink-0">
              <img
                src="/logo_ipfluencer_horizontal.png"
                alt="IPfluencer"
                className="block w-[108px] h-auto rounded opacity-90"
              />
            </a>
            <a href="https://www.certifafrica.com/" target="_blank" rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()} title="CertifAfrica" className="shrink-0">
              <img
                src="/logo_certifafrica_horizontal.png"
                alt="CertifAfrica"
                className="block w-[108px] h-auto rounded opacity-90"
              />
            </a>
          </div>
        )}
        <button className="about-btn flex items-center gap-[7px] w-full border border-[#e0e0e0] bg-transparent rounded-md px-2.5 py-[7px] mb-1.5 cursor-pointer text-xs text-[#888] transition-colors duration-[130ms] hover:bg-[#f4f9ff] hover:text-[#29ABE2] hover:border-[#b8dff5] group" title={String(tr.about)}
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); setShowAbout(true); }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 opacity-70 group-hover:opacity-100">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="8.5"/>
            <line x1="12" y1="12" x2="12" y2="16"/>
          </svg>
          <span>{String(tr.about)}</span>
        </button>
        {freeMode ? (
          <button
            className="flex items-center gap-[7px] w-full border border-[#2e9e5e] bg-[#2e9e5e] rounded-md px-2.5 py-[7px] cursor-pointer text-xs text-white font-semibold transition-colors duration-[130ms] hover:bg-[#26854f] hover:border-[#26854f]"
            title={String(tr.upgradeBtn)}
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); setShowUpgrade(true); }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <polygon points="12 2 15 8 22 9 17 14 18 21 12 18 6 21 7 14 2 9 9 8 12 2" />
            </svg>
            <span>{String(tr.upgradeBtn)}</span>
          </button>
        ) : (
          <button className="logout-btn flex items-center gap-[7px] w-full border border-[#e0e0e0] bg-transparent rounded-md px-2.5 py-[7px] cursor-pointer text-xs text-[#888] transition-colors duration-[130ms] hover:bg-[#fff0ee] hover:text-[#c0392b] hover:border-[#f5c6c0] group" title={String(tr.logout)}
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onLogout?.(); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 opacity-70 group-hover:opacity-100">
              <path d="M18.36 6.64a9 9 0 1 1-12.73 0"/>
              <line x1="12" y1="2" x2="12" y2="12"/>
            </svg>
            <span>{String(tr.logout)}</span>
          </button>
        )}
      </div>

      {/* Context menu */}
      {ctxMenu.show && ctxMenu.target && (
        <div className="fixed bg-white border border-[#ddd] rounded-md shadow-md z-[1000] min-w-[160px] py-1" style={{ top: ctxMenu.y, left: ctxMenu.x }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          {ctxMenu.target.type === 'folder' && (
            <>
              <div className="px-3.5 py-[7px] cursor-pointer text-[13px] whitespace-nowrap hover:bg-[#f0f0f0]" onClick={() => { setCtxMenu((c: CtxMenuLocal) => ({ ...c, show: false })); startCreate(ctxMenu.target!.id, 'folder'); }}>{String(tr.ctxNewFolder)}</div>
              <div className="px-3.5 py-[7px] cursor-pointer text-[13px] whitespace-nowrap hover:bg-[#f0f0f0]" onClick={() => { setCtxMenu((c: CtxMenuLocal) => ({ ...c, show: false })); startCreate(ctxMenu.target!.id, 'project'); }}>{String(tr.ctxNewProject)}</div>
              <div className="px-3.5 py-[7px] cursor-pointer text-[13px] whitespace-nowrap hover:bg-[#f0f0f0] flex items-center gap-2" onClick={() => { setCtxMenu((c: CtxMenuLocal) => ({ ...c, show: false })); startCreate(ctxMenu.target!.id, 'whiteboard'); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2.5" ry="2.5"/>
                  <path d="M6 15 Q9 11 12 14 Q15 17 18 12" strokeWidth="2.2"/>
                  <line x1="15" y1="4" x2="19" y2="0.5"/>
                  <rect x="17.5" y="-0.5" width="5" height="3" rx="0.7" transform="rotate(35 17.5 -0.5)"/>
                </svg>
                {String(tr.ctxNewWhiteboard)}
              </div>
              <div className="px-3.5 py-[7px] cursor-pointer text-[13px] whitespace-nowrap hover:bg-[#f0f0f0] flex items-center gap-2" onClick={() => { setCtxMenu((c: CtxMenuLocal) => ({ ...c, show: false })); startCreate(ctxMenu.target!.id, 'textdoc'); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="8" y1="13" x2="16" y2="13"/>
                  <line x1="8" y1="17" x2="16" y2="17"/>
                  <line x1="8" y1="9" x2="11" y2="9"/>
                </svg>
                {String(tr.ctxNewTextDoc)}
              </div>
              <div className="h-px bg-[#eee] my-1" />
            </>
          )}
          <div className="px-3.5 py-[7px] cursor-pointer text-[13px] whitespace-nowrap hover:bg-[#f0f0f0]" onClick={() => renameItem(ctxMenu.target!)}>{String(tr.ctxRename)}</div>
          {ctxMenu.target.type === 'project' && (
            <div className="px-3.5 py-[7px] cursor-pointer text-[13px] whitespace-nowrap hover:bg-[#f0f0f0]" onClick={() => duplicateItem(ctxMenu.target!)}>{String(tr.ctxDuplicate)}</div>
          )}
          {ctxMenu.target.id !== '__inbox__' && (
            <div className="px-3.5 py-[7px] cursor-pointer text-[13px] whitespace-nowrap text-[#cc2200] hover:bg-[#f0f0f0]" onClick={() => deleteItem(ctxMenu.target!)}>{String(tr.ctxDelete)}</div>
          )}
        </div>
      )}
    </div>
  );
}
